/**
 * Policy Violation Consolidation Service
 * 
 * Consolidates multiple policy violations of the same type into summarized messages
 * to reduce redundancy and improve clarity in the UI.
 */

import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { PolicyCheckType } from '@/types/timesheetSettings';

export const VIOLATION_TYPE_PRIORITY: Record<string, number> = {
  'legal-daily-maximum': 0,
  'legal-weekly-maximum': 0,
  'daily-overage': 1,
  'weekly-overage': 1,
  'missing-hours': 2,
  'daily-missing-hours': 2,
  'weekly-missing-hours': 2,
  'non-working-day': 3,
  'daily-non-working-day': 3,
  'weekly-non-working-day': 3,
};

export interface ConsolidatedViolation {
  id: string;
  type: PolicyCheckType;
  severity: 'info' | 'warning' | 'error';
  count: number;
  summary: string;
  details: Array<{
    day?: string;
    date?: string;
    message: string;
    actual: number;
    expected: number;
    limit?: number;
    threshold?: number;
  }>;
  insight: string;
  originalViolations: PolicyViolation[];
}

/**
 * Group violations by type
 */
function groupViolationsByType(violations: PolicyViolation[]): Map<PolicyCheckType, PolicyViolation[]> {
  const grouped = new Map<PolicyCheckType, PolicyViolation[]>();
  
  violations.forEach(violation => {
    const existing = grouped.get(violation.violationType) || [];
    existing.push(violation);
    grouped.set(violation.violationType, existing);
  });
  
  return grouped;
}

/**
 * Get the highest severity from a list of violations
 */
function getHighestSeverity(violations: PolicyViolation[]): 'info' | 'warning' | 'error' {
  if (violations.some(v => v.severity === 'error')) return 'error';
  if (violations.some(v => v.severity === 'warning')) return 'warning';
  return 'info';
}

/**
 * Get day name from date string
 */
function getDayName(dateStr: string): string {
  const date = new Date(dateStr);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
}

/**
 * Format violation type as human-readable title
 */
function formatViolationType(type: PolicyCheckType): string {
  const typeMap: Record<PolicyCheckType, string> = {
    'daily-overage': 'Daily Limit Violations',
    'weekly-overage': 'Weekly Total',
    'legal-daily-maximum': 'Legal Daily Maximum Violations',
    'legal-weekly-maximum': 'Legal Weekly Maximum',
    'missing-hours': 'Missing Hours',
    'non-working-day': 'Non-Working Day Activity',
    'daily-missing-hours': 'Missing Hours',
    'weekly-missing-hours': 'Missing Hours',
    'daily-non-working-day': 'Non-Working Day Activity',
    'weekly-non-working-day': 'Non-Working Day Activity',
  };
  
  return typeMap[type] || type;
}

/**
 * Generate insight message based on violation type and count
 */
function generateInsight(type: PolicyCheckType, count: number, severity: 'info' | 'warning' | 'error'): string {
  if (count === 1) {
    // Single violation - provide specific guidance
    switch (type) {
      case 'daily-overage':
      case 'legal-daily-maximum':
        return '💡 Ensure overtime is approved and necessary.';
      case 'weekly-overage':
      case 'legal-weekly-maximum':
        return '💡 Ensure overtime is approved and necessary.';
      case 'missing-hours':
        return '💡 Review your calendar and add any missing time entries.';
      case 'non-working-day':
        return '💡 Ensure this is approved if outside your normal schedule.';
      default:
        return '💡 Please review and adjust your schedule.';
    }
  }
  
  // Multiple violations - provide consolidated guidance
  switch (type) {
    case 'daily-overage':
    case 'legal-daily-maximum':
      if (severity === 'error') {
        return '💡 Multiple days exceed organisation policy. Please review and adjust your schedule immediately.';
      }
      return '💡 Multiple days exceed expected hours. Ensure overtime is approved and necessary.';
    case 'weekly-overage':
    case 'legal-weekly-maximum':
      if (severity === 'error') {
        return '💡 Weekly total violates policy. You must reduce hours before submission.';
      }
      return '💡 Weekly total exceeds expected hours. Ensure overtime is approved and necessary.';
    case 'missing-hours':
      return '💡 Review your calendar and add missing time entries for these days.';
    case 'non-working-day':
      return '💡 Multiple non-working days have activity. Ensure this is approved if outside your normal schedule.';
    default:
      return '💡 Multiple policy violations detected. Please review and adjust your schedule.';
  }
}

/**
 * Create consolidated violation for daily violations
 */
function consolidateDailyViolations(
  type: PolicyCheckType,
  violations: PolicyViolation[]
): ConsolidatedViolation {
  const severity = getHighestSeverity(violations);
  const details = violations.map(v => ({
    day: v.date ? getDayName(v.date) : undefined,
    date: v.date,
    message: `${v.details.actual.toFixed(2)}h exceeds ${
      v.details.limit 
        ? `${v.details.limit}h daily limit` 
        : v.details.threshold 
          ? `${v.details.threshold.toFixed(1)}h threshold (expected ${v.details.expected.toFixed(1)}h)`
          : `expected ${v.details.expected.toFixed(1)}h`
    }`,
    actual: v.details.actual,
    expected: v.details.expected,
    limit: v.details.limit,
    threshold: v.details.threshold,
  }));
  
  // Sort by day of week
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  details.sort((a, b) => {
    if (!a.day || !b.day) return 0;
    return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
  });
  
  const summary = `${formatViolationType(type)} (${violations.length} occurrence${violations.length > 1 ? 's' : ''})`;
  const insight = generateInsight(type, violations.length, severity);
  
  return {
    id: `consolidated-${type}-${violations[0].id}`,
    type,
    severity,
    count: violations.length,
    summary,
    details,
    insight,
    originalViolations: violations,
  };
}

/**
 * Create consolidated violation for weekly violations
 */
function consolidateWeeklyViolations(
  type: PolicyCheckType,
  violations: PolicyViolation[]
): ConsolidatedViolation {
  const severity = getHighestSeverity(violations);
  const violation = violations[0]; // Should only be one weekly violation
  
  const details = [{
    message: `${violation.details.actual.toFixed(2)}h exceeds ${
      violation.details.limit 
        ? `${violation.details.limit}h weekly limit` 
        : violation.details.threshold 
          ? `${violation.details.threshold.toFixed(1)}h threshold (expected ${violation.details.expected.toFixed(1)}h)`
          : `expected ${violation.details.expected.toFixed(1)}h`
    }`,
    actual: violation.details.actual,
    expected: violation.details.expected,
    limit: violation.details.limit,
    threshold: violation.details.threshold,
  }];
  
  const summary = formatViolationType(type);
  const insight = generateInsight(type, 1, severity);
  
  return {
    id: `consolidated-${type}-${violation.id}`,
    type,
    severity,
    count: 1,
    summary,
    details,
    insight,
    originalViolations: violations,
  };
}

/**
 * Create consolidated violation for other violation types
 */
function consolidateOtherViolations(
  type: PolicyCheckType,
  violations: PolicyViolation[]
): ConsolidatedViolation {
  const severity = getHighestSeverity(violations);
  const details = violations.map(v => ({
    day: v.date ? getDayName(v.date) : undefined,
    date: v.date,
    message: v.message,
    actual: v.details.actual,
    expected: v.details.expected,
    limit: v.details.limit,
    threshold: v.details.threshold,
  }));
  
  const summary = violations.length > 1
    ? `${formatViolationType(type)} (${violations.length} occurrence${violations.length > 1 ? 's' : ''})`
    : formatViolationType(type);
  const insight = generateInsight(type, violations.length, severity);
  
  return {
    id: `consolidated-${type}-${violations[0].id}`,
    type,
    severity,
    count: violations.length,
    summary,
    details,
    insight,
    originalViolations: violations,
  };
}

/**
 * Consolidate policy violations by type
 * 
 * Groups violations of the same type and creates summarized messages
 * to reduce redundancy in the UI.
 * 
 * @param violations - Array of policy violations to consolidate
 * @returns Array of consolidated violations
 */
export function consolidatePolicyViolations(
  violations: PolicyViolation[]
): ConsolidatedViolation[] {
  if (violations.length === 0) return [];
  
  const grouped = groupViolationsByType(violations);
  const consolidated: ConsolidatedViolation[] = [];
  
  // Process each violation type
  grouped.forEach((typeViolations, type) => {
    // Determine if this is a daily, weekly, or other type of violation
    const isDailyType = [
      'daily-overage',
      'legal-daily-maximum',
      'non-working-day',
    ].includes(type);
    
    const isWeeklyType = [
      'weekly-overage',
      'legal-weekly-maximum',
    ].includes(type);
    
    if (typeViolations.length === 1) {
      // Single violation - still consolidate for consistent format
      if (isDailyType) {
        consolidated.push(consolidateDailyViolations(type, typeViolations));
      } else if (isWeeklyType) {
        consolidated.push(consolidateWeeklyViolations(type, typeViolations));
      } else {
        consolidated.push(consolidateOtherViolations(type, typeViolations));
      }
    } else {
      // Multiple violations - consolidate
      if (isDailyType) {
        consolidated.push(consolidateDailyViolations(type, typeViolations));
      } else if (isWeeklyType) {
        // Weekly violations should typically be single, but handle multiple just in case
        consolidated.push(consolidateWeeklyViolations(type, typeViolations));
      } else {
        consolidated.push(consolidateOtherViolations(type, typeViolations));
      }
    }
  });
  
  // Sort by severity (error > warning > info), then by violation type priority
  consolidated.sort((a, b) => {
    const severityOrder = { error: 0, warning: 1, info: 2 };
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    const priorityA = VIOLATION_TYPE_PRIORITY[a.type] ?? 99;
    const priorityB = VIOLATION_TYPE_PRIORITY[b.type] ?? 99;
    return priorityA - priorityB;
  });
  
  return consolidated;
}

/**
 * Check if consolidation should be applied
 * 
 * Consolidation is applied when there are 2 or more violations of the same type
 * 
 * @param violations - Array of policy violations
 * @returns True if consolidation should be applied
 */
export function shouldConsolidate(violations: PolicyViolation[]): boolean {
  if (violations.length < 2) return false;
  
  const grouped = groupViolationsByType(violations);
  
  // Check if any type has multiple violations
  for (const [_, typeViolations] of grouped) {
    if (typeViolations.length >= 2) {
      return true;
    }
  }
  
  return false;
}
